import { isStripeCheckout } from '../../../lib/checkout-mode.js';
import { useState } from 'react';
import { 
  ArrowRight, 
  Check, 
  ShoppingCart, 
  Star, 
  ShieldCheck, 
  Zap, 
  Sparkles,
  Package
} from 'lucide-react';
import { Button } from '../../../components/ui/Button.jsx';
import { Card, CardContent } from '../../../components/ui/Card.jsx';
import { formatCurrency } from '../../../lib/utils.js';

export function SplitFeaturedHero({
  tenant,
  catalogCount = null,
  onSearch,
  onSelectCategory,
  onNavigate,
  featuredProduct = null,
  onAddToCart,
  onViewProduct,
  heroTitle,
  heroSubtitle,
  heroCtaPrimary,
  heroCtaSecondary,
}) {
  const [added, setAdded] = useState(false);

  // Use only verified featured hardware product if provided
  const product = (featuredProduct && typeof featuredProduct === 'object' && featuredProduct.id) ? featuredProduct : null;

  const handleNav = (path) => {
    if (onNavigate) onNavigate(path);
  };

  const handleSpotlightAddToCart = (e) => {
    e.stopPropagation();
    if (!product) return;
    if (onAddToCart) {
      onAddToCart(product);
    }
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const handleSpotlightDetail = () => {
    if (!product) return;
    if (onViewProduct) {
      onViewProduct(product.slug || product.sku || product.id);
    } else if (onNavigate) {
      onNavigate(`/products/${product.slug || product.sku || product.id}`);
    }
  };

  const unitPriceCents = product
    ? (product.price?.cents !== undefined
        ? Number(product.price.cents)
        : (product.base_price !== undefined ? Number(product.base_price) : 0))
    : 0;
  // Unpriced spotlight articles must not present a fake 0-EUR wholesale
  // price — show "Auf Anfrage" instead. Genuine positive prices unchanged.
  const isSpotlightBuyable = unitPriceCents > 0;
  const formattedPrice = product && isSpotlightBuyable
    ? (product.price?.formatted || formatCurrency(unitPriceCents))
    : 'Auf Anfrage';

  let specs = {};
  if (product) {
    try {
      specs = product.specs_json ? JSON.parse(product.specs_json) : (product.specs || {});
    } catch {
      specs = {};
    }
  }

  const hasCatalogCount = typeof catalogCount === 'number' && catalogCount > 0;

  return (
    <section
      data-hero-variant="split_featured"
      className="relative overflow-hidden rounded-3xl border border-[var(--border)] bg-gradient-to-br from-white via-[var(--surface-container-low,#f8fafc)] to-white p-5 sm:p-10 lg:p-14 shadow-sm"
    >
      <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-center min-w-0">
        
        {/* Left Side: Partner Statement & Mission */}
        <div className="space-y-6 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-[var(--primary,#e3000b)]/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-[var(--primary,#e3000b)]">
              <Sparkles className="h-3.5 w-3.5" />
              <span>Partner-Spotlight</span>
            </span>
            <span className="inline-flex items-center rounded-full border border-[var(--border)] bg-white px-3 py-1 text-xs font-medium text-[var(--secondary)]">
              {tenant?.name || 'ASL Partner-Shop'}
            </span>
          </div>

          <h1 className="font-display text-3xl font-extrabold tracking-tight text-[var(--foreground)] sm:text-5xl lg:text-6xl leading-[1.1] break-words">
            {heroTitle ? (
              heroTitle
            ) : (
              <>
                Ihr starker Partner für <br />
                <span className="text-[var(--primary,#e3000b)]">Sicherheitstechnik</span>.
              </>
            )}
          </h1>

          <p className="text-sm sm:text-base leading-relaxed text-[var(--secondary)] max-w-xl">
            {heroSubtitle ? (
              heroSubtitle
            ) : (
              <>
                Als Fachpartner bieten wir Ihnen direkten Zugriff auf{' '}
                <strong className="text-[var(--foreground)] font-semibold">
                  {hasCatalogCount
                    ? `${catalogCount.toLocaleString('de-DE')} Fachartikel`
                    : 'B2B-Sicherheitstechnik'}
                </strong>{' '}
                zu Fachpartner-Konditionen.
              </>
            )}
          </p>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center pt-2">
            <Button
              size="lg"
              className="h-12 px-6 text-sm sm:text-base font-bold shadow-sm cursor-pointer"
              onClick={() => handleNav('/catalog')}
            >
              {heroCtaPrimary || <span>Gesamten Katalog durchsuchen</span>}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="h-12 px-6 text-sm sm:text-base font-medium cursor-pointer"
              onClick={() => handleNav('/cart')}
            >
              {heroCtaSecondary || <span>Projekt anfragen</span>}
            </Button>
          </div>

          <div className="pt-6 grid grid-cols-3 gap-3 border-t border-[var(--border)] text-center sm:text-left">
            <div>
              <p className="font-display text-xl font-extrabold text-[var(--foreground)]">
                {hasCatalogCount ? catalogCount.toLocaleString('de-DE') : 'B2B'}
              </p>
              <p className="text-xs text-[var(--secondary)] font-medium">B2B Artikel</p>
            </div>
            <div>
              <p className="font-display text-xl font-extrabold text-[var(--primary)]">B2B</p>
              <p className="text-xs text-[var(--secondary)] font-medium">Fachsortiment</p>
            </div>
            <div>
              <p className="font-display text-xl font-extrabold text-[var(--foreground)]">B2B</p>
              <p className="text-xs text-[var(--secondary)] font-medium">Fachkonditionen</p>
            </div>
          </div>
        </div>

        {/* Right Side: Spotlight Product Card or Clean Catalog Showcase */}
        <div className="relative min-w-0">
          {product ? (
            <>
              <div className="absolute -top-3 right-4 z-20">
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-3 py-1 text-xs font-extrabold text-white shadow-md">
                  <Star className="h-3 w-3 fill-white" />
                  <span>Hardware-Highlight</span>
                </span>
              </div>

              <Card
                onClick={handleSpotlightDetail}
                className="group overflow-hidden border-2 border-[var(--primary)]/20 bg-white p-4 sm:p-6 shadow-xl transition-all hover:border-[var(--primary)] hover:shadow-2xl cursor-pointer"
              >
                <div className="space-y-4">

                  {/* Header tags */}
                  <div className="flex items-center justify-between">
                    <span className="font-display text-xs font-extrabold uppercase tracking-wider text-[var(--primary)]">
                      {product.manufacturer || 'Fachhandel'}
                    </span>
                    <span className="font-mono text-xs text-[var(--secondary)] font-semibold">
                      Art.-Nr. {product.sku}
                    </span>
                  </div>

                  {/* Product preview & Title */}
                  <div className="space-y-2">
                    <h3 className="font-display text-lg sm:text-xl font-bold text-[var(--foreground)] group-hover:text-[var(--primary)] transition-colors line-clamp-2">
                      {product.name}
                    </h3>
                    <p className="text-xs text-[var(--secondary)] line-clamp-2 leading-relaxed">
                      {product.short_description || product.shortDescription || product.description || ''}
                    </p>
                  </div>

                  {/* Technical attributes chips */}
                  {specs && Object.keys(specs).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {Object.entries(specs).slice(0, 4).map(([key, val]) => (
                        <span
                          key={key}
                          className="rounded-md border border-[var(--border)] bg-[var(--surface-container-low,#f8fafc)] px-2 py-0.5 text-xs font-mono text-[var(--secondary)] truncate max-w-full"
                        >
                          {String(val)}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Pricing & 1-Click Cart Addition */}
                  <div className="flex items-center justify-between border-t border-[var(--border)] pt-4 mt-2">
                    <div>
                      <span className="text-xs font-bold uppercase tracking-widest text-[var(--secondary)] block">
                        Preis
                      </span>
                      <span className="font-display text-2xl font-extrabold text-[var(--foreground)]">
                        {formattedPrice}
                      </span>
                    </div>

                    <Button
                      size="default"
                      variant={added ? 'secondary' : 'default'}
                      onClick={handleSpotlightAddToCart}
                      className="h-11 px-4 font-bold gap-2 shadow-sm cursor-pointer"
                      aria-label={isStripeCheckout(tenant) ? 'Highlight in den Warenkorb legen' : 'Highlight in den Anfragekorb legen'}
                    >
                      {added ? (
                        <>
                          <Check className="h-4 w-4 text-emerald-600" />
                          <span>Hinzugefügt</span>
                        </>
                      ) : (
                        <>
                          <ShoppingCart className="h-4 w-4" />
                          <span>1-Klick Anfrage</span>
                        </>
                      )}
                    </Button>
                  </div>

                </div>
              </Card>
            </>
          ) : (
            <Card className="border border-[var(--border)] bg-white/90 p-8 shadow-sm">
              <div className="space-y-6">
                <div className="flex items-center gap-3 border-b border-[var(--border)] pb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--primary)]/10 text-[var(--primary)]">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-display text-base font-bold text-[var(--foreground)]">
                      B2B-Fachsortiment
                    </h3>
                    <p className="text-xs text-[var(--secondary)]">
                      Direktzugriff auf das B2B-Fachsortiment
                    </p>
                  </div>
                </div>

                <p className="text-xs sm:text-sm text-[var(--secondary)] leading-relaxed">
                  Durchsuchen Sie den Fachkatalog nach Warengruppen, Herstellern oder Artikelnummern für Spezifikationen und Netto-Konditionen.
                </p>

                <div className="pt-2">
                  <Button
                    size="default"
                    className="w-full h-11 font-bold shadow-xs cursor-pointer"
                    onClick={() => handleNav('/catalog')}
                  >
                    <span>Fachkatalog durchsuchen</span>
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>

      </div>
    </section>
  );
}
